import { useCallback } from "react"
import { useDiscussions } from "@/services/store/useDiscussions.ts"
import { useSelection } from "@/services/store/useSelection.ts"
import { useChangeRequest } from "@/services/store/useChangeRequest.ts"
import { NewCommentBubble } from "@/models/Discussions"
import { calculateRowTopPosition } from "@/components/designSystem/Discussions/utils"
import { createNewBaseComment } from "@/components/designSystem/FCC/utils/createNewBaseComment"

export interface CommentCreationPlacementData {
  lineNumber: number
  filePath: string
}

export const useNewCommentBubble = () => {
  const { injectNewCommentBubbleToDiscussions, toggleManualRecalculationTrigger, setCurrentOpenedDiscussion } =
    useDiscussions()
  const { selectedTableFilePath, firstSelectedSide, selectedText, selectedLines, clearLines } = useSelection()
  const { endLine, startLine } = selectedLines
  const { breakingChangeItemToBeShown } = useChangeRequest()

  const handleAddCommentClick = useCallback(
    async (data?: CommentCreationPlacementData) => {
      const startLineNumber = data
        ? data.lineNumber
        : breakingChangeItemToBeShown?.codeLocations[0]
          ? breakingChangeItemToBeShown.codeLocations[0].lineNumber
          : startLine
      const endLineNumber = data
        ? data.lineNumber
        : breakingChangeItemToBeShown?.codeLocations[0]
          ? breakingChangeItemToBeShown.codeLocations[0].lineNumber
          : endLine
      const filePathValue = data
        ? data.filePath
        : breakingChangeItemToBeShown?.codeLocations[0]
          ? breakingChangeItemToBeShown?.codeLocations[0].fileName
          : selectedTableFilePath

      if (!startLineNumber && !endLineNumber) {
        console.error("Trying to add a comment bubble without any line numbers")
        return
      }

      if (!filePathValue) {
        console.error("Trying to add a comment bubble without file path")
        return
      }

      const bubbleBase = createNewBaseComment({
        filePathValue,
        startLineNumber,
        endLineNumber,
        firstSelectedSide,
        selectedText,
        selectedLines
      })

      const calculatedPosition = calculateRowTopPosition({
        ...bubbleBase
      })

      const newBubbleData: NewCommentBubble = {
        ...bubbleBase,
        topPosition: calculatedPosition || startLineNumber || undefined
      }

      const newBubble = injectNewCommentBubbleToDiscussions(newBubbleData)

      setCurrentOpenedDiscussion(newBubble)
      toggleManualRecalculationTrigger()
      clearLines()
    },
    [
      breakingChangeItemToBeShown,
      startLine,
      injectNewCommentBubbleToDiscussions,
      selectedTableFilePath,
      endLine,
      selectedText,
      selectedLines,
      firstSelectedSide,
      setCurrentOpenedDiscussion,
      toggleManualRecalculationTrigger,
      clearLines
    ]
  )

  return { handleAddCommentClick }
}
